import { z } from 'zod'

import type { CommonResponseSliceNoticeParentResponse } from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { mapSliceContent, type Slice, sliceSchema } from '~/schema/slice'
import {
  flattenStrapiItem,
  isLastStrapiPage,
  strapiItemSchema,
  strapiPageSchema,
} from '~/schema/strapi'

import {
  type AcademyNotice,
  AcademyNoticeAttributesSchema,
  type Banner,
  BannerAttributesSchema,
  type Faq,
  FaqAttributesSchema,
  type HomeButtonLinks,
  HomeButtonLinksAttributesSchema,
  type NoticeBoard,
  NoticeBoardAttributesSchema,
  type OnboardingPopup,
  OnboardingPopupAttributesSchema,
  type ParentsAppNotice,
  ParentsAppNoticeSchema,
  type Review,
  ReviewAttributesSchema,
  type ServerMaintenance,
  ServerMaintenanceAttributesSchema,
  type StudentNoticePopup,
  StudentNoticePopupAttributesSchema,
  type UpdatedContent,
  UpdatedContentAttributesSchema,
  type VodColumn,
  VodColumnAttributesSchema,
} from './schemas'

const ENTITY = 'Notice'

export const adaptParentsAppNotices = (
  raw: CommonResponseSliceNoticeParentResponse['data'],
): Slice<ParentsAppNotice> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(ParentsAppNoticeSchema), raw, ENTITY),
    (notice) => notice,
  )

// Strapi 응답은 생성 타입이 없어 입력을 unknown 으로 받는다. 검증 뒤 모양은 스키마가 정한다.
const adaptStrapiList = <Attributes extends object, Item>(
  attributes: z.ZodType<Attributes>,
  raw: unknown,
  map: (item: { id: number } & Attributes) => Item,
): Item[] =>
  (parseApiResponse(z.array(strapiItemSchema(attributes)), raw, ENTITY) ?? []).map((item) =>
    map(flattenStrapiItem(item)),
  )

export const adaptAcademyNotices = (raw: unknown): { content: AcademyNotice[]; last: boolean } => {
  const page = parseApiResponse(strapiPageSchema(AcademyNoticeAttributesSchema), raw, ENTITY)
  return {
    content: (page?.data ?? []).map(flattenStrapiItem),
    last: isLastStrapiPage(page?.meta?.pagination),
  }
}

export const adaptFaqs = (raw: unknown): Faq[] =>
  adaptStrapiList(FaqAttributesSchema, raw, (faq) => faq)

export const adaptNoticeBoards = (raw: unknown): NoticeBoard[] =>
  adaptStrapiList(NoticeBoardAttributesSchema, raw, ({ media, ...notice }) => ({
    ...notice,
    media: (media?.data ?? []).map(({ attributes }) => attributes),
  }))

export const adaptStudentNoticePopups = (raw: unknown): StudentNoticePopup[] =>
  adaptStrapiList(StudentNoticePopupAttributesSchema, raw, (popup) => popup)

export const adaptOnboardingPopups = (raw: unknown): OnboardingPopup[] =>
  adaptStrapiList(OnboardingPopupAttributesSchema, raw, (popup) => popup)

export const adaptBanners = (raw: unknown): Banner[] =>
  adaptStrapiList(BannerAttributesSchema, raw, (banner) => banner)

export const adaptUpdatedContents = (raw: unknown): UpdatedContent[] =>
  adaptStrapiList(UpdatedContentAttributesSchema, raw, (content) => content)

export const adaptReviews = (raw: unknown): Review[] =>
  adaptStrapiList(ReviewAttributesSchema, raw, ({ thumbnail, ...review }) => ({
    ...review,
    thumbnail: thumbnail?.data?.attributes,
  }))

export const adaptVodColumns = (raw: unknown): VodColumn[] =>
  adaptStrapiList(
    VodColumnAttributesSchema,
    raw,
    ({ thumbnail, thumbnail_x, author_thumbnail, pdf, ...vodColumn }) => ({
      ...vodColumn,
      thumbnail: thumbnail?.data?.attributes ?? null,
      thumbnail_x: thumbnail_x?.data?.attributes ?? null,
      author_thumbnail: author_thumbnail?.data?.attributes ?? null,
      pdf: pdf?.data?.attributes ?? null,
    }),
  )

export const adaptHomeButtonLinks = (raw: unknown): HomeButtonLinks[] =>
  adaptStrapiList(HomeButtonLinksAttributesSchema, raw, (links) => links)

export const adaptServerMaintenance = (raw: unknown): ServerMaintenance =>
  parseApiResponse(strapiItemSchema(ServerMaintenanceAttributesSchema), raw, ENTITY)?.attributes
